// @ts-check
// =============================================================================
// js/app/progression.js — single-player challenge-ladder progression.
//
// WHAT THIS IS
//   Per-hero bot tiers 1–20 (challenge-ladder.md). Beating a bot at your
//   current tier unlocks the next; progress persists in localStorage exactly
//   like the old client (LoadChallengeLevels/SaveChallengeLevels :7093).
//   Index = the BOT's hero id (12 heroes).
//
//   recordBotResult() ports the old Duel Update win check (:6723): winning a
//   tiered bot duel at (or above) your recorded level bumps the level, capped
//   at 20; winning an untiered duel just unlocks tier 1.
// =============================================================================

const STORAGE_KEY = 'challengeLevels';

/**
 * Load the per-hero tier levels (0 = nothing beaten yet).
 * @returns {number[]}  12 entries, one per hero.
 */
export function loadChallengeLevels() {
	try {
		const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
		if (Array.isArray(stored)) {
			// Pad/trim to exactly 12 heroes.
			return Array.from({ length: 12 }, (_, i) => Number(stored[i]) || 0);
		}
	} catch {
		// fall through to fresh levels
	}
	return new Array(12).fill(0);
}

/**
 * Persist the levels.
 * @param {number[]} levels
 * @returns {void}
 */
export function saveChallengeLevels(levels) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(levels));
}

/**
 * Apply a finished bot duel to the ladder (call when the human won).
 * @param {any} duel  The final duel state (player1 = the human viewer).
 * @returns {void}
 */
export function recordBotResult(duel) {
	if (!duel?.player1?.winner || duel.player2?.id !== 'Bot' || duel.spectating) return;
	const heroId = duel.player2.hero;
	const levels = loadChallengeLevels();
	if (heroId == null || heroId < 0 || heroId >= levels.length) return;

	if (!duel.player2.tier) {
		// An untiered win unlocks the ladder at tier 1.
		levels[heroId] = Math.max(levels[heroId], 1);
	} else if (duel.player2.tier >= levels[heroId] && levels[heroId] < 20) {
		// Beating your current (or a higher) tier climbs one rung.
		levels[heroId] += 1;
	}
	saveChallengeLevels(levels);
}
